import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { getTeamColor } from '@/lib/dashboard-utils.jsx';

export function useDashboardData() {
  const [kpis, setKpis] = useState({ totalUsers: 0, dau: 0, totalPhotos: 0, totalQuizAnswers: 0 });
  const [engagementData, setEngagementData] = useState([]);
  const [activityData, setActivityData] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [filterPending, setFilterPending] = useState(true);
  const [loading, setLoading] = useState(true);
  const [questionnaireData, setQuestionnaireData] = useState([]);
  const [questionnaireStats, setQuestionnaireStats] = useState({ completed: 0 });
  const [cacheStatus, setCacheStatus] = useState([]);
  const [cacheMetrics, setCacheMetrics] = useState({ leaguesCovered: 0, teamsWithStats: 0, freshestCache: null, stalestCache: null });
  const [socialMetrics, setSocialMetrics] = useState({ twitter: {}, tiktok: {}, instagram: {}, facebook: {} });
  const [socialTrendData, setSocialTrendData] = useState([]);
  const [chatAnalytics, setChatAnalytics] = useState({
    kpis: { totalConversations: 0, totalMessages: 0, avgMessagesPerConv: 0, activeTeams: 0 },
    teamVolume: [], teamEngagement: [], activityTimeline: [], hasData: false
  });
  const [quizAnalytics, setQuizAnalytics] = useState({
    kpis: { quizCompletions: 0, quizAccuracy: 0, questionnaireCompletions: 0, mostPopularTier: 'N/A' },
    quizByTeam: [], questionnaireByTeam: [], tierBreakdown: [], teamEngagementTable: [], weeklyActivity: [], hasData: false
  });
  const [quizFilters, setQuizFilters] = useState({ dateRange: 'all', sport: 'all', country: 'all', team: 'all', tier: 'all' });
  const [showAllTeams, setShowAllTeams] = useState(false);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [availableTiers, setAvailableTiers] = useState([]);
  const [securityData, setSecurityData] = useState({ events: [], totalConversations: 0, unresolvedCount: 0, geminiKeyInUse: 1 });

  const fetchKPIs = async () => {
    try {
      const [usersRes, photosRes, quizRes] = await Promise.all([
        supabase.from('users').select('*', { count: 'exact', head: true }),
        supabase.from('fanzcam_photos').select('*', { count: 'exact', head: true }),
        supabase.from('quiz_responses').select('*', { count: 'exact', head: true })
      ]);
      setKpis({ totalUsers: usersRes.count || 0, dau: 0, totalPhotos: photosRes.count || 0, totalQuizAnswers: quizRes.count || 0 });
    } catch (error) {
      console.error('Error fetching KPIs:', error);
    }
  };

  const fetchEngagementByTeam = async () => {
    try {
      const { data: photos, error: photoError } = await supabase.from('fanzcam_photos').select('team_id');
      const { data: quizzes, error: quizError } = await supabase.from('quiz_responses').select('team_id');
      if (photoError || quizError) throw photoError || quizError;
      const teamIds = [...new Set([...photos.map(p => p.team_id), ...quizzes.map(q => q.team_id)])];
      const { data: teams } = await supabase.from('teams').select('id, name').in('id', teamIds);
      const teamMap = {};
      teams?.forEach(team => { teamMap[team.id] = { name: team.name, photos: 0, quizzes: 0, id: team.id }; });
      photos.forEach(p => { if (teamMap[p.team_id]) teamMap[p.team_id].photos++; });
      quizzes.forEach(q => { if (teamMap[q.team_id]) teamMap[q.team_id].quizzes++; });
      const chartData = Object.values(teamMap).map(team => ({
        name: team.name, photos: team.photos, quizzes: team.quizzes,
        total: team.photos + team.quizzes, isHighlighted: team.id === 611 || team.id === 645
      })).sort((a, b) => b.total - a.total);
      setEngagementData(chartData);
    } catch (error) {
      console.error('Error fetching engagement data:', error);
    }
  };

  const fetchActivityPulse = async () => {
    try {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const { data: photos } = await supabase.from('fanzcam_photos').select('created_at').gte('created_at', sevenDaysAgo.toISOString());
      const { data: quizzes } = await supabase.from('quiz_responses').select('created_at').gte('created_at', sevenDaysAgo.toISOString());
      const dateMap = {};
      for (let i = 6; i >= 0; i--) {
        const date = new Date(); date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        dateMap[dateStr] = { date: dateStr, photos: 0, quizzes: 0 };
      }
      photos?.forEach(p => { const d = p.created_at.split('T')[0]; if (dateMap[d]) dateMap[d].photos++; });
      quizzes?.forEach(q => { const d = q.created_at.split('T')[0]; if (dateMap[d]) dateMap[d].quizzes++; });
      setActivityData(Object.values(dateMap).map(d => ({
        date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        uploads: d.photos, quizzes: d.quizzes
      })));
    } catch (error) {
      console.error('Error fetching activity data:', error);
    }
  };

  const fetchPhotos = async () => {
    try {
      let query = supabase.from('fanzcam_photos').select('*').limit(50);
      if (filterPending) query = query.eq('status', 'pending');
      const { data, error } = await query;
      if (error) throw error;
      const sortedData = (data || []).sort((a, b) => {
        if (a.priority === 'high' && b.priority !== 'high') return -1;
        if (a.priority !== 'high' && b.priority === 'high') return 1;
        return new Date(b.created_at) - new Date(a.created_at);
      });
      setPhotos(sortedData);
    } catch (error) {
      console.error('Error fetching photos:', error);
    }
  };

  const fetchSocialMetrics = async () => {
    try {
      const { data, error } = await supabase.from('social_metrics').select('*').order('recorded_at', { ascending: true });
      if (error) { setSocialMetrics({ twitter: {}, tiktok: {}, instagram: {}, facebook: {} }); return; }
      if (!data || data.length === 0) { setSocialMetrics({ twitter: {}, tiktok: {}, instagram: {}, facebook: {} }); return; }
      const platforms = { twitter: {}, tiktok: {}, instagram: {}, facebook: {} };
      data.forEach(metric => {
        const platform = metric.platform.toLowerCase();
        if (platforms[platform]) {
          platforms[platform][metric.metric_name] = metric.metric_value;
          platforms[platform][`${metric.metric_name}_updated`] = metric.recorded_at;
        }
      });
      Object.keys(platforms).forEach(platform => {
        const p = platforms[platform];
        if (platform === 'twitter' && p.impressions_7d && p.impressions_7d > 0) {
          p.engagement_rate = (((p.likes_7d || 0) + (p.retweets_7d || 0) + (p.replies_7d || 0)) / p.impressions_7d * 100).toFixed(2);
        }
        if (platform === 'tiktok' && p.videos_7d && p.videos_7d > 0) {
          p.avg_views_per_video = Math.round(p.views_7d / p.videos_7d);
        }
        p.follower_growth = p.follower_growth || 0;
      });
      setSocialMetrics(platforms);
      const { data: trendData, error: trendError } = await supabase.from('social_metrics').select('*')
        .in('metric_name', ['followers', 'page_likes']).order('recorded_at', { ascending: true });
      if (!trendError && trendData) {
        const weeklyData = {};
        trendData.forEach(metric => {
          const week = new Date(metric.recorded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          if (!weeklyData[week]) weeklyData[week] = { week };
          if (metric.platform === 'twitter') weeklyData[week].twitter = metric.metric_value;
          if (metric.platform === 'tiktok') weeklyData[week].tiktok = metric.metric_value;
          if (metric.platform === 'instagram') weeklyData[week].instagram = metric.metric_value;
          if (metric.platform === 'facebook') weeklyData[week].facebook = metric.metric_value;
        });
        setSocialTrendData(Object.values(weeklyData).slice(-8));
      }
    } catch (error) {
      console.error('Error fetching social metrics:', error);
      setSocialMetrics({ twitter: {}, tiktok: {}, instagram: {}, facebook: {} });
    }
  };

  const fetchChatAnalytics = async () => {
    try {
      const { data, error } = await supabase.from('zecho_conversations').select('*');
      if (error) { setChatAnalytics(prev => ({ ...prev, hasData: false })); return; }
      if (!data || data.length === 0) { setChatAnalytics(prev => ({ ...prev, hasData: false })); return; }
      const uniqueSessions = new Set(data.map(d => d.session_id));
      const totalConversations = uniqueSessions.size;
      const userMessages = data.filter(d => d.role === 'user');
      const totalMessages = userMessages.length;
      const avgMessagesPerConv = totalConversations > 0 ? (totalMessages / totalConversations).toFixed(1) : 0;
      const activeTeams = new Set(data.map(d => d.team_slug)).size;
      const teamVolumeMap = {};
      data.forEach(msg => {
        const key = msg.team_slug;
        if (!teamVolumeMap[key]) teamVolumeMap[key] = { team_name: msg.team_name || msg.team_slug, team_slug: key, conversations: new Set() };
        teamVolumeMap[key].conversations.add(msg.session_id);
      });
      const teamVolume = Object.values(teamVolumeMap)
        .map(t => ({ team_name: t.team_name, team_slug: t.team_slug, conversations: t.conversations.size, color: getTeamColor(t.team_slug) }))
        .sort((a, b) => b.conversations - a.conversations);
      const teamEngagementMap = {};
      data.forEach(msg => {
        const key = msg.team_slug;
        if (!teamEngagementMap[key]) teamEngagementMap[key] = { team_name: msg.team_name || msg.team_slug, team_slug: key, sessions: new Set(), user_messages: [], all_messages: [], dow_counts: {} };
        teamEngagementMap[key].sessions.add(msg.session_id);
        teamEngagementMap[key].all_messages.push(msg);
        if (msg.role === 'user') teamEngagementMap[key].user_messages.push(msg);
        const dow = new Date(msg.created_at).getDay();
        teamEngagementMap[key].dow_counts[dow] = (teamEngagementMap[key].dow_counts[dow] || 0) + 1;
      });
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const teamEngagement = Object.values(teamEngagementMap).map(t => {
        const avgLength = t.user_messages.length > 0 ? Math.round(t.user_messages.reduce((sum, m) => sum + (m.message_length || 0), 0) / t.user_messages.length) : 0;
        const mostActiveDow = Object.entries(t.dow_counts).sort((a, b) => b[1] - a[1])[0]?.[0] || 0;
        const lastChat = t.all_messages.length > 0 ? new Date(Math.max(...t.all_messages.map(m => new Date(m.created_at)))) : null;
        return { team_name: t.team_name, team_slug: t.team_slug, conversations: t.sessions.size, messages: t.user_messages.length, avg_length: avgLength, most_active_day: dayNames[mostActiveDow], last_chat: lastChat, color: getTeamColor(t.team_slug) };
      }).sort((a, b) => b.conversations - a.conversations);
      const thirtyDaysAgo = new Date(); thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const recentData = data.filter(d => new Date(d.created_at) >= thirtyDaysAgo);
      const timelineMap = {};
      recentData.forEach(msg => {
        const day = new Date(msg.created_at).toISOString().split('T')[0];
        if (!timelineMap[day]) timelineMap[day] = { day, sessions: new Set(), users: new Set() };
        timelineMap[day].sessions.add(msg.session_id);
        if (msg.user_id) timelineMap[day].users.add(msg.user_id);
      });
      const activityTimeline = Object.values(timelineMap)
        .map(t => ({ day: new Date(t.day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), conversations: t.sessions.size, unique_users: t.users.size }))
        .sort((a, b) => new Date(a.day) - new Date(b.day));
      setChatAnalytics({ kpis: { totalConversations, totalMessages, avgMessagesPerConv, activeTeams }, teamVolume, teamEngagement, activityTimeline, hasData: true });
    } catch (error) {
      console.error('Error fetching chat analytics:', error);
      setChatAnalytics(prev => ({ ...prev, hasData: false }));
    }
  };

  const fetchQuizAnalytics = async () => {
    try {
      let quizQuery = supabase.from('quiz_responses').select('*');
      let questionnaireQuery = supabase.from('questionnaire_responses').select('*');
      if (quizFilters.dateRange !== 'all') {
        const now = new Date(); let startDate;
        if (quizFilters.dateRange === '7d') startDate = new Date(now - 7 * 24 * 60 * 60 * 1000);
        else if (quizFilters.dateRange === '30d') startDate = new Date(now - 30 * 24 * 60 * 60 * 1000);
        else if (quizFilters.dateRange === '90d') startDate = new Date(now - 90 * 24 * 60 * 60 * 1000);
        if (startDate) { quizQuery = quizQuery.gte('created_at', startDate.toISOString()); questionnaireQuery = questionnaireQuery.gte('created_at', startDate.toISOString()); }
      }
      const [{ data: quizData, error: quizError }, { data: questData, error: questError }] = await Promise.all([quizQuery, questionnaireQuery]);
      if (quizError && questError) { setQuizAnalytics(prev => ({ ...prev, hasData: false })); return; }
      const quizzes = quizData || []; const questionnaires = questData || [];
      if (quizzes.length === 0 && questionnaires.length === 0) { setQuizAnalytics(prev => ({ ...prev, hasData: false })); return; }
      const quizCompletions = quizzes.length;
      const quizAccuracy = quizzes.length > 0 ? (quizzes.reduce((sum, q) => sum + (q.score || 0), 0) / quizzes.length).toFixed(1) : 0;
      const questionnaireCompletions = questionnaires.filter(q => q.completed).length;
      const tierMap = {};
      quizzes.forEach(q => { const tier = q.tier || q.difficulty || 'Standard'; tierMap[tier] = (tierMap[tier] || 0) + 1; });
      const tierBreakdown = Object.entries(tierMap).map(([name, count]) => ({ name, count, percentage: quizzes.length > 0 ? ((count / quizzes.length) * 100).toFixed(1) : 0 })).sort((a, b) => b.count - a.count);
      const mostPopularTier = tierBreakdown.length > 0 ? tierBreakdown[0].name : 'N/A';
      const teamIds = [...new Set([...quizzes.map(q => q.team_id), ...questionnaires.map(q => q.team_id)].filter(Boolean))];
      let teamNameMap = {};
      if (teamIds.length > 0) { const { data: teams } = await supabase.from('teams').select('id, name').in('id', teamIds); teams?.forEach(t => { teamNameMap[t.id] = t.name; }); }
      // Populate available teams and tiers for filter dropdowns
      const teamOptions = Object.entries(teamNameMap).map(([id, name]) => ({ id: Number(id), name })).sort((a, b) => a.name.localeCompare(b.name));
      setAvailableTeams(teamOptions);
      const tierOptions = [...new Set(quizzes.map(q => q.tier || q.difficulty || 'Standard'))].sort();
      setAvailableTiers(tierOptions);

      let filteredTeamIds = null;
      if (quizFilters.sport !== 'all' || quizFilters.country !== 'all') {
        let teamQuery = supabase.from('teams').select('id, sport, country');
        if (quizFilters.sport !== 'all') teamQuery = teamQuery.eq('sport', quizFilters.sport);
        if (quizFilters.country !== 'all') teamQuery = teamQuery.eq('country', quizFilters.country);
        const { data: filteredTeams } = await teamQuery;
        filteredTeamIds = new Set((filteredTeams || []).map(t => t.id));
      }
      const filterByTeam = (items) => { if (!filteredTeamIds) return items; return items.filter(item => filteredTeamIds.has(item.team_id)); };
      let filteredQuizzes = filterByTeam(quizzes); let filteredQuestionnaires = filterByTeam(questionnaires);

      // Apply team filter
      if (quizFilters.team !== 'all') {
        const teamId = Number(quizFilters.team);
        filteredQuizzes = filteredQuizzes.filter(q => q.team_id === teamId);
        filteredQuestionnaires = filteredQuestionnaires.filter(q => q.team_id === teamId);
      }

      // Apply tier filter
      if (quizFilters.tier !== 'all') {
        filteredQuizzes = filteredQuizzes.filter(q => (q.tier || q.difficulty || 'Standard') === quizFilters.tier);
      }
      const quizByTeamMap = {};
      filteredQuizzes.forEach(q => { const key = q.team_id; if (!key) return; if (!quizByTeamMap[key]) quizByTeamMap[key] = { team_id: key, count: 0 }; quizByTeamMap[key].count++; });
      const quizByTeam = Object.values(quizByTeamMap).map(t => ({ ...t, team_name: teamNameMap[t.team_id] || `Team ${t.team_id}`, color: getTeamColor(teamNameMap[t.team_id]?.toLowerCase().replace(/\s+/g, '_')) })).sort((a, b) => b.count - a.count);
      const questByTeamMap = {};
      filteredQuestionnaires.forEach(q => { const key = q.team_id || 'unknown'; if (!questByTeamMap[key]) questByTeamMap[key] = { team_id: key, count: 0 }; questByTeamMap[key].count++; });
      const questionnaireByTeam = Object.values(questByTeamMap).map(t => ({ ...t, team_name: teamNameMap[t.team_id] || (t.team_id === 'unknown' ? 'Unassigned' : `Team ${t.team_id}`) })).sort((a, b) => b.count - a.count);
      const combinedTeamMap = {};
      filteredQuizzes.forEach(q => { const key = q.team_id; if (!key) return; if (!combinedTeamMap[key]) combinedTeamMap[key] = { team_id: key, team_name: teamNameMap[key] || `Team ${key}`, quizzes: 0, questionnaires: 0, totalScore: 0, quizScoreCount: 0, color: getTeamColor(teamNameMap[key]?.toLowerCase().replace(/\s+/g, '_')) }; combinedTeamMap[key].quizzes++; if (q.score !== undefined && q.score !== null) { combinedTeamMap[key].totalScore += q.score; combinedTeamMap[key].quizScoreCount++; } });
      filteredQuestionnaires.forEach(q => { const key = q.team_id || 'unknown'; if (!combinedTeamMap[key]) combinedTeamMap[key] = { team_id: key, team_name: teamNameMap[key] || (key === 'unknown' ? 'Unassigned' : `Team ${key}`), quizzes: 0, questionnaires: 0, totalScore: 0, quizScoreCount: 0, color: getTeamColor(teamNameMap[key]?.toLowerCase().replace(/\s+/g, '_')) }; combinedTeamMap[key].questionnaires++; });
      const teamEngagementTable = Object.values(combinedTeamMap).map(t => ({ ...t, avgScore: t.quizScoreCount > 0 ? (t.totalScore / t.quizScoreCount).toFixed(1) : 'N/A', total: t.quizzes + t.questionnaires })).sort((a, b) => b.total - a.total);
      const weekMap = {};
      const allItems = [...filteredQuizzes.map(q => ({ ...q, type: 'quiz' })), ...filteredQuestionnaires.map(q => ({ ...q, type: 'questionnaire' }))];
      allItems.forEach(item => { if (!item.created_at) return; const d = new Date(item.created_at); const weekStart = new Date(d); weekStart.setDate(weekStart.getDate() - weekStart.getDay()); const key = weekStart.toISOString().split('T')[0]; if (!weekMap[key]) weekMap[key] = { week: key, quizzes: 0, questionnaires: 0 }; if (item.type === 'quiz') weekMap[key].quizzes++; else weekMap[key].questionnaires++; });
      const weeklyActivity = Object.values(weekMap).sort((a, b) => a.week.localeCompare(b.week)).slice(-8).map(w => ({ ...w, week: new Date(w.week).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) }));
      setQuizAnalytics({
        kpis: { quizCompletions: filteredTeamIds ? filteredQuizzes.length : quizCompletions, quizAccuracy: Number(quizAccuracy), questionnaireCompletions: filteredTeamIds ? filteredQuestionnaires.filter(q => q.completed).length : questionnaireCompletions, mostPopularTier },
        quizByTeam, questionnaireByTeam, tierBreakdown, teamEngagementTable, weeklyActivity, hasData: true
      });
    } catch (error) {
      console.error('Error fetching quiz analytics:', error);
      setQuizAnalytics(prev => ({ ...prev, hasData: false }));
    }
  };

  const fetchCacheStatus = async () => {
    try {
      // Fetch from ALL sport-specific standings tables
      const currentSeason = new Date().getMonth() >= 6 ? new Date().getFullYear() : new Date().getFullYear() - 1;
      const basketballSeason = `${currentSeason}-${currentSeason + 1}`;

      const [
        { data: standingsData, error: standingsError },
        { data: statsData, error: statsError },
        { data: basketballData, error: basketballError },
        { data: hockeyData, error: hockeyError },
      ] = await Promise.all([
        supabase.from('league_standings').select('league_id, league_name, league_country, last_updated').eq('season', currentSeason),
        supabase.from('detailed_team_stats').select('league_id, last_updated').eq('season', currentSeason),
        supabase.from('basketball_standings').select('league_id, team_name, updated_at'),
        supabase.from('hockey_standings').select('league_id, league_name, last_updated'),
      ]);
      if (standingsError) throw standingsError;
      if (statsError) throw statsError;
      // Don't throw on basketball/hockey errors — tables might not exist yet
      if (basketballError) console.warn('basketball_standings query failed:', basketballError.message);
      if (hockeyError) console.warn('hockey_standings query failed:', hockeyError.message);

      // Sport type determines which table to check for standings
      const leagueConfig = [
        // ─── Football ───
        { id: 203, name: 'Super Lig', country: 'Turkey', flag: '', priority: 1, sport: 'football' },
        { id: 144, name: 'Jupiler Pro League', country: 'Belgium', flag: '', priority: 1, sport: 'football' },
        { id: 39, name: 'Premier League', country: 'England', flag: '', priority: 2, sport: 'football' },
        { id: 140, name: 'La Liga', country: 'Spain', flag: '', priority: 2, sport: 'football' },
        { id: 135, name: 'Serie A', country: 'Italy', flag: '', priority: 2, sport: 'football' },
        { id: 78, name: 'Bundesliga', country: 'Germany', flag: '', priority: 2, sport: 'football' },
        { id: 61, name: 'Ligue 1', country: 'France', flag: '', priority: 2, sport: 'football' },
        { id: 88, name: 'Eredivisie', country: 'Netherlands', flag: '', priority: 3, sport: 'football' },
        { id: 128, name: 'Liga Profesional', country: 'Argentina', flag: '', priority: 3, sport: 'football' },
        { id: 94, name: 'Primeira Liga', country: 'Portugal', flag: '', priority: 3, sport: 'football' },
        { id: 71, name: 'Brasileirao Serie A', country: 'Brazil', flag: '', priority: 3, sport: 'football' },
        { id: 119, name: 'Superligaen', country: 'Denmark', flag: '', priority: 3, sport: 'football' },
        { id: 103, name: 'Eliteserien', country: 'Norway', flag: '', priority: 3, sport: 'football' },
        { id: 244, name: 'Veikkausliiga', country: 'Finland', flag: '', priority: 3, sport: 'football' },
        { id: 113, name: 'Allsvenskan', country: 'Sweden', flag: '', priority: 3, sport: 'football' },
        { id: 2, name: 'Champions League', country: 'Europe', flag: '', priority: 4, sport: 'football' },
        { id: 3, name: 'Europa League', country: 'Europe', flag: '', priority: 4, sport: 'football' },
        { id: 848, name: 'Conference League', country: 'Europe', flag: '', priority: 4, sport: 'football' },
        { id: 13, name: 'Copa Libertadores', country: 'South America', flag: '', priority: 5, sport: 'football' },
        { id: 11, name: 'Copa Sudamericana', country: 'South America', flag: '', priority: 5, sport: 'football' },
        { id: 253, name: 'MLS', country: 'USA/Canada', flag: '', priority: 6, sport: 'football' },
        { id: 5, name: 'Nations League', country: 'Europe', flag: '', priority: 7, sport: 'football' },
        { id: 32, name: 'Euro Qualifiers', country: 'Europe', flag: '', priority: 7, sport: 'football' },
        { id: 1, name: 'World Cup', country: 'World', flag: '', priority: 8, sport: 'football' },
        // ─── Basketball ───
        { id: 12, name: 'NBA', country: 'USA/Canada', flag: '', priority: 6, sport: 'basketball' },
        { id: 13, name: 'WNBA', country: 'USA/Canada', flag: '', priority: 6, sport: 'basketball' },
        { id: 104, name: 'BSL (Turkey)', country: 'Turkey', flag: '', priority: 7, sport: 'basketball' },
        { id: 105, name: 'KBSL (Turkey)', country: 'Turkey', flag: '', priority: 7, sport: 'basketball' },
        { id: 120, name: 'EuroLeague', country: 'Europe', flag: '', priority: 7, sport: 'basketball' },
        { id: 359, name: 'EuroLeague Women', country: 'Europe', flag: '', priority: 7, sport: 'basketball' },
        { id: 117, name: 'Liga ACB', country: 'Spain', flag: '', priority: 7, sport: 'basketball' },
        { id: 202, name: 'BCL', country: 'Europe', flag: '', priority: 7, sport: 'basketball' },
        // ─── Hockey ───
        { id: 57, name: 'NHL', country: 'USA/Canada', flag: '', priority: 6, sport: 'hockey' },
        { id: 58, name: 'AHL', country: 'USA/Canada', flag: '', priority: 7, sport: 'hockey' },
        { id: 261, name: 'PWHL', country: 'USA/Canada', flag: '', priority: 7, sport: 'hockey' },
      ];

      const getStatus = (lastUpdate) => {
        if (!lastUpdate) return { status: 'missing', label: 'Missing', color: 'red' };
        const hoursDiff = (new Date() - lastUpdate) / (1000 * 60 * 60);
        if (hoursDiff < 48) return { status: 'fresh', label: 'Fresh', color: 'green' };
        if (hoursDiff < 168) return { status: 'stale', label: 'Stale', color: 'yellow' };
        return { status: 'missing', label: 'Missing', color: 'red' };
      };

      const processedStatus = leagueConfig.map(league => {
        let leagueStandings, standingsLastUpdate, leagueStats, statsLastUpdate;

        if (league.sport === 'basketball') {
          // Basketball standings use updated_at instead of last_updated
          leagueStandings = (basketballData || []).filter(s => s.league_id === league.id);
          standingsLastUpdate = leagueStandings.length > 0 ? new Date(Math.max(...leagueStandings.map(s => new Date(s.updated_at)))) : null;
          leagueStats = leagueStandings; // Basketball doesn't have separate detailed stats
          statsLastUpdate = standingsLastUpdate;
        } else if (league.sport === 'hockey') {
          leagueStandings = (hockeyData || []).filter(s => s.league_id === league.id);
          standingsLastUpdate = leagueStandings.length > 0 ? new Date(Math.max(...leagueStandings.map(s => new Date(s.last_updated)))) : null;
          leagueStats = leagueStandings; // Hockey doesn't have separate detailed stats
          statsLastUpdate = standingsLastUpdate;
        } else {
          // Football — use league_standings + detailed_team_stats
          leagueStandings = (standingsData || []).filter(s => s.league_id === league.id);
          standingsLastUpdate = leagueStandings.length > 0 ? new Date(Math.max(...leagueStandings.map(s => new Date(s.last_updated)))) : null;
          leagueStats = (statsData || []).filter(s => s.league_id === league.id);
          statsLastUpdate = leagueStats.length > 0 ? new Date(Math.max(...leagueStats.map(s => new Date(s.last_updated)))) : null;
        }

        // Use the best available timestamp for the freshness timeline bar.
        // European/cup leagues (UCL, UEL, UECL, Copa Lib…) only have league_standings rows —
        // no detailed_team_stats — so statsLastUpdate is null; fall back to standingsLastUpdate.
        const bestTimestamp = statsLastUpdate || standingsLastUpdate;
        return { ...league, standingsStatus: getStatus(standingsLastUpdate), statsStatus: getStatus(statsLastUpdate), teamsCached: leagueStats.length, lastRefresh: bestTimestamp, daysAgo: bestTimestamp ? (new Date() - bestTimestamp) / (1000 * 60 * 60 * 24) : null };
      });
      processedStatus.sort((a, b) => a.name.localeCompare(b.name));
      setCacheStatus(processedStatus);

      // Aggregate metrics across all sports
      const allStandingsIds = [
        ...new Set([
          ...(standingsData || []).map(s => s.league_id),
          ...(basketballData || []).map(s => s.league_id),
          ...(hockeyData || []).map(s => s.league_id),
        ])
      ];
      const leaguesCovered = allStandingsIds.length;
      const teamsWithStats = (statsData?.length || 0) + (basketballData?.length || 0) + (hockeyData?.length || 0);
      const allUpdates = [
        ...(standingsData || []).map(d => new Date(d.last_updated)),
        ...(statsData || []).map(d => new Date(d.last_updated)),
        ...(basketballData || []).map(d => new Date(d.updated_at)),
        ...(hockeyData || []).map(d => new Date(d.last_updated)),
      ].filter(d => !isNaN(d));
      const freshest = allUpdates.length > 0 ? new Date(Math.max(...allUpdates)) : null;
      const stalest = allUpdates.length > 0 ? new Date(Math.min(...allUpdates)) : null;
      let stalestLeague = null;
      if (stalest) { const stalestEntry = processedStatus.find(l => l.lastRefresh && Math.abs(l.lastRefresh - stalest) < 1000); stalestLeague = stalestEntry?.name || 'Unknown'; }
      setCacheMetrics({ leaguesCovered, teamsWithStats, freshestCache: freshest, stalestCache: stalest, stalestLeague });
    } catch (error) {
      console.error('Error fetching cache status:', error);
    }
  };

  const fetchQuestionnaireData = async () => {
    try {
      const { data, error } = await supabase.from('questionnaire_responses').select('*').eq('completed', true).order('total_score', { ascending: false });
      if (error) throw error;
      setQuestionnaireData(data || []);
      setQuestionnaireStats({ completed: (data || []).length });
    } catch (error) {
      console.error('Error fetching questionnaire data:', error);
    }
  };

  const handleModeration = async (photoId, newStatus) => {
    try {
      setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, status: newStatus } : p));
      const { data, error } = await supabase.from('fanzcam_photos').update({ status: newStatus }).eq('id', photoId).select();
      if (error) throw error;
      if (!data || data.length === 0) {
        setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, status: 'pending' } : p));
        toast.error('Update blocked - check Supabase RLS policies for fanzcam_photos');
        return;
      }
      toast.success(newStatus === 'approved' ? 'Photo approved!' : 'Photo rejected!', { duration: 2000 });
      fetchPhotos(); fetchKPIs();
    } catch (error) {
      console.error('Error updating photo status:', error);
      setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, status: 'pending' } : p));
      toast.error(`Failed to update photo status: ${error.message || 'Unknown error'}`);
    }
  };

  const fetchSecurityData = async () => {
    try {
      const fortyEightHoursAgo = new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString();
      const [eventsRes, convsRes] = await Promise.all([
        supabase.from('security_events').select('*').gte('created_at', fortyEightHoursAgo).order('created_at', { ascending: false }).limit(50),
        supabase.from('zecho_conversations').select('*', { count: 'exact', head: true }),
      ]);
      const events = eventsRes.data || [];
      const unresolvedCount = events.filter(e => !e.resolved && e.severity !== 'info').length;
      setSecurityData({
        events,
        totalConversations: convsRes.count || 0,
        unresolvedCount,
        geminiKeyInUse: 1,
      });
    } catch (error) {
      console.error('Error fetching security data:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchKPIs(), fetchEngagementByTeam(), fetchActivityPulse(), fetchPhotos(), fetchQuestionnaireData(), fetchCacheStatus(), fetchSocialMetrics(), fetchChatAnalytics(), fetchQuizAnalytics(), fetchSecurityData()]);
      setLoading(false);
    };
    loadData();
    const cacheInterval = setInterval(fetchCacheStatus, 5 * 60 * 1000);
    const socialInterval = setInterval(fetchSocialMetrics, 30 * 60 * 1000);
    const chatInterval = setInterval(fetchChatAnalytics, 5 * 60 * 1000);
    const quizInterval = setInterval(fetchQuizAnalytics, 5 * 60 * 1000);
    const securityInterval = setInterval(fetchSecurityData, 60 * 1000); // every 60s
    return () => { clearInterval(cacheInterval); clearInterval(socialInterval); clearInterval(chatInterval); clearInterval(quizInterval); clearInterval(securityInterval); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchPhotos(); }, [filterPending]);
  useEffect(() => { fetchQuizAnalytics(); }, [quizFilters]);

  // Compute data health statuses
  const dataHealth = [
    { id: 'kpis', name: 'KPIs', status: kpis.totalUsers > 0 || kpis.totalPhotos > 0 ? 'live' : 'partial', detail: kpis.totalUsers > 0 ? `${kpis.totalUsers} users` : 'No users' },
    { id: 'engagement', name: 'Engagement', status: engagementData.length > 0 ? 'live' : 'partial', detail: engagementData.length > 0 ? `${engagementData.length} teams` : 'No data' },
    { id: 'questionnaires', name: 'Questionnaires', status: questionnaireData.length > 0 ? 'live' : 'partial', detail: questionnaireData.length > 0 ? `${questionnaireStats.completed} done` : 'No responses' },
    { id: 'social', name: 'Social Reach', status: socialMetrics.twitter.followers ? 'live' : 'setup', detail: socialMetrics.twitter.followers ? '4 platforms' : 'Run SQL setup' },
    { id: 'chat', name: 'Zecho Chat', status: chatAnalytics.hasData ? 'live' : 'setup', detail: chatAnalytics.hasData ? `${chatAnalytics.kpis.totalConversations} convs` : 'Run SQL setup' },
    { id: 'quiz', name: 'Quiz Analytics', status: quizAnalytics.hasData ? 'live' : 'partial', detail: quizAnalytics.hasData ? `${quizAnalytics.kpis.quizCompletions} quizzes` : 'No quiz data' },
    { id: 'cache', name: 'Cache Status', status: cacheStatus.length > 0 ? 'live' : 'partial', detail: cacheStatus.length > 0 ? `${cacheMetrics.leaguesCovered}/28 leagues` : 'No cache data' },
    { id: 'moderation', name: 'Moderation', status: photos.length > 0 ? 'live' : 'partial', detail: photos.length > 0 ? `${photos.length} photos` : 'No photos' },
    {
      id: 'apis', name: 'API Subs', status: 'live', detail: 'apis', apis: [
        { sport: 'Football', status: 'live' },
        { sport: 'Basketball', status: 'live' },
        { sport: 'Volleyball', status: 'live' },
        { sport: 'Hockey', status: 'live' },
        { sport: 'Am. Football', status: 'planned' },
        { sport: 'Baseball', status: 'planned' }
      ]
    }
  ];

  return {
    loading, kpis, engagementData, activityData,
    photos, filterPending, setFilterPending, handleModeration,
    questionnaireData, questionnaireStats,
    socialMetrics, socialTrendData,
    chatAnalytics,
    quizAnalytics, quizFilters, setQuizFilters, showAllTeams, setShowAllTeams,
    availableTeams, availableTiers,
    cacheStatus, cacheMetrics,
    securityData,
    dataHealth
  };
}
